import React, { useState, useEffect } from "react";
import { generateDocument, apiFetch } from "../services/api";
import DocumentPreview from "./DocumentPreview";
import Notification from "./Notification";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

const GenerateDocumentButton = ({
  entityId,
  entityName,
  variant = "button",
  /** desktop | mobile — mobile uses compact "Document" label */
  density = "desktop",
  /** Optional id for programmatic click (mobile action tray) */
  triggerId = undefined,
}) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [showDocumentPreview, setShowDocumentPreview] = useState(false);
  const [documentPreviewData, setDocumentPreviewData] = useState(null);

  // Fetch trial status to check document generation limits
  // Use event system to avoid duplicate API calls - listen for updates from App.js
  useEffect(() => {
    const fetchTrialStatus = async () => {
      try {
        const response = await apiFetch("/api/auth/trial-status");
        if (response.ok) {
          const data = await response.json();
          setTrialStatus(data);
        }
      } catch (error) {
        console.error("Error fetching trial status:", error);
      }
    };

    // Listen for trial status updates from other components
    const handleStatusUpdate = (event) => {
      if (event.detail) {
        setTrialStatus(event.detail);
      }
    };

    // Try to get from localStorage first (set by App.js)
    const cachedStatus = localStorage.getItem("trialStatus");
    if (cachedStatus) {
      try {
        setTrialStatus(JSON.parse(cachedStatus));
      } catch (e) {
        // If parsing fails, fetch fresh
        fetchTrialStatus();
      }
    } else {
      // Only fetch if not in cache
      fetchTrialStatus();
    }

    window.addEventListener("trialStatusUpdated", handleStatusUpdate);
    return () => {
      window.removeEventListener("trialStatusUpdated", handleStatusUpdate);
    };
  }, []);

  const handleGenerateDocument = async (format = "docx") => {
    if (!entityId) {
      setNotification({
        message: "Entity ID is required",
        type: "error",
        duration: 3000,
      });
      return;
    }

    // Check document generation limit before generating
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const isSuperUser = user?.is_super_user || false;
    
    if (!isSuperUser && trialStatus) {
      const available = trialStatus.available?.document_generation ?? 0;
      const limit = trialStatus.limits?.document_generation ?? 0;
      
      if (available <= 0 && limit !== -1) {
        setNotification({
          message: `You have reached your document generation limit. In trial mode, you can generate up to ${limit} documents. You have ${available} remaining. Please contact your administrator to increase your limits.`,
          type: "error",
          duration: 8000,
        });
        return;
      }
    }

    setIsGenerating(true);
    setError(null);

    // Show notification that generation has started
    const formatName = format === "markdown" ? "Markdown" : format === "docx" ? "Word Document" : "PDF";
    setNotification({
      message: `Generating ${formatName}... This may take a moment.`,
      type: "info",
      duration: 0, // Don't auto-close, we'll close it when done
    });

    try {
      const result = await generateDocument(entityId, {
        kind: "solution_blueprint",
        format,
      });

      // Close the "generating" notification
      setNotification(null);

      const templateHint = result.usedTenantTemplate
        ? ` Using organisation template${
            result.templateName ? `: ${result.templateName}` : ""
          }.`
        : result.templateName
          ? ` Using ${result.templateName}.`
          : "";

      // For markdown, show preview with quality information
      if (!result.isBinary && result.document && (result.quality_score !== undefined || result.recommendations)) {
        setDocumentPreviewData({
          document: result.document,
          qualityScore: result.quality_score || 100,
          recommendations: result.recommendations || [],
          sections: result.sections || [],
          templateName: result.templateName,
          usedTenantTemplate: result.usedTenantTemplate,
        });
        setShowDocumentPreview(true);
      } else {
        // For binary formats or if no quality data, download directly
        if (result.isBinary) {
          // Handle binary formats (DOCX, PDF)
          const url = window.URL.createObjectURL(result.blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = result.filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        } else {
          // Handle markdown without quality data
          const blob = new Blob([result.document], { type: "text/markdown" });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = `${entityName || "document"}_Solution_Blueprint.md`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }

        // Show success notification
        setNotification({
          message: `${formatName} generated successfully! Download started.${templateHint}`,
          type: "success",
          duration: 4000,
        });
      }

      // Refresh trial status after successful generation
      try {
        const response = await apiFetch("/api/auth/trial-status");
        if (response.ok) {
          const data = await response.json();
          setTrialStatus(data);
        }
      } catch (error) {
        console.error("Error refreshing trial status:", error);
      }
    } catch (err) {
      console.error("Error generating document:", err);
      setError(err.message || "Failed to generate document");
      // Close the "generating" notification
      setNotification(null);
      
      // Check if it's a limit error
      const errorMessage = err.message || "Unknown error";
      if (errorMessage.includes("limit") || errorMessage.includes("reached")) {
        // Refresh trial status to get updated count
        try {
          const response = await apiFetch("/api/auth/trial-status");
          if (response.ok) {
            const data = await response.json();
            setTrialStatus(data);
          }
        } catch (error) {
          console.error("Error refreshing trial status:", error);
        }
      }
      
      // Show error notification
      setNotification({
        message: `Failed to generate document: ${errorMessage}`,
        type: "error",
        duration: 8000,
      });
    } finally {
      setIsGenerating(false);
    }
  };

  // Check if document generation is available
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const isSuperUser = user?.is_super_user || false;
  const docGenLimit = trialStatus?.limits?.document_generation ?? 0;
  const docGenAvailable = trialStatus?.available?.document_generation ?? null;
  const isDocGenDisabled = !isSuperUser && docGenAvailable !== null && docGenAvailable <= 0 && docGenLimit !== -1;

  const isMenuItem = variant === "menuItem";
  const isMobileDensity = density === "mobile";
  const buttonClassName = isMenuItem
    ? `w-full text-left px-3 py-2 text-sm flex items-center gap-2 ${
        isGenerating || isDocGenDisabled
          ? "text-slate-400 cursor-not-allowed"
          : "text-slate-700 hover:bg-slate-50"
      }`
    : isMobileDensity
      ? `px-2.5 py-1.5 text-xs rounded-lg transition-colors flex items-center gap-1.5 ${
          isGenerating || isDocGenDisabled
            ? "bg-gray-600 text-white opacity-70 cursor-not-allowed"
            : "bg-green-600 hover:bg-green-700 text-white"
        }`
      : `px-3 py-1 text-sm rounded-lg transition-colors flex items-center gap-2 ${
          isGenerating || isDocGenDisabled
            ? "bg-gray-600 text-white opacity-70 cursor-not-allowed"
            : "bg-green-600 hover:bg-green-700 text-white"
        }`;

  return (
    <div className={isMenuItem ? "" : "relative"}>
      {isDocGenDisabled ? (
        <div
          className={
            isMenuItem
              ? "px-3 py-2 text-sm text-amber-800"
              : "px-3 py-1 text-sm text-yellow-400 bg-yellow-900/20 border border-yellow-600/50 rounded-lg"
          }
        >
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
            <div>
              <div className="font-medium">Limit reached ({docGenAvailable} remaining)</div>
              <div className={isMenuItem ? "text-xs text-amber-700 mt-0.5" : "text-xs text-yellow-300 mt-0.5"}>
                In trial mode: {docGenLimit} documents max
              </div>
            </div>
          </div>
        </div>
      ) : (
        <button
          type="button"
          id={triggerId}
          onClick={() => handleGenerateDocument("docx")}
          disabled={isGenerating || isDocGenDisabled}
          className={buttonClassName}
          title={
            !isSuperUser && docGenAvailable !== null && docGenLimit !== -1
              ? `In trial mode, you can generate up to ${docGenLimit} documents. You have ${docGenAvailable} remaining.`
              : "Generate Solution Blueprint Document"
          }
        >
        {isGenerating ? (
          <>
            <svg
              className="animate-spin w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            Generating...
          </>
        ) : (
          <>
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            {isMobileDensity ? "Document" : "Generate Document"}
            {!isMobileDensity &&
              !isSuperUser &&
              docGenAvailable !== null &&
              docGenLimit !== -1 && (
              <span className="text-xs opacity-75 ml-1">
                ({docGenAvailable} left)
              </span>
            )}
          </>
        )}
      </button>
      )}

      {/* Notification for document generation */}
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          duration={notification.duration}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Document Preview Modal */}
      {showDocumentPreview && documentPreviewData && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.75)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            overflow: "auto",
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDocumentPreview(false);
            }
          }}
        >
          <div
            style={{
              backgroundColor: "#111827",
              borderRadius: "0.5rem",
              width: "100%",
              maxWidth: "1200px",
              maxHeight: "90vh",
              overflow: "auto",
              padding: "1.5rem",
              border: "1px solid #374151",
              position: "relative",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <DocumentPreview
              documentData={documentPreviewData.document}
              qualityScore={documentPreviewData.qualityScore}
              recommendations={documentPreviewData.recommendations}
              entityName={entityName || "Entity"}
              onClose={() => setShowDocumentPreview(false)}
              onDownload={async (format) => {
                // Download the document in the selected format
                setShowDocumentPreview(false);
                // Small delay to allow modal to close
                setTimeout(async () => {
                  await handleGenerateDocument(format);
                }, 100);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default GenerateDocumentButton;
